import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MaklanMonogram from "./MaklanMonogram";

const NAV = [
  { to: "/inventory", label: "Inventory" },
  { to: "/compare", label: "Compare" },
  { to: "/import", label: "Import" },
  { to: "/track", label: "Track" },
  { to: "/partner", label: "Partner" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050814]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 group">
          <MaklanMonogram size={28} tone="gold" />
          <div className="leading-none">
            <div
              className="font-display uppercase text-white text-[1.6rem] leading-none"
              style={{ letterSpacing: "0.28em", paddingLeft: "0.28em" }}
            >
              Maklan
            </div>
            <div
              className="text-[10px] uppercase text-white/45 mt-1.5"
              style={{ letterSpacing: "0.42em", paddingLeft: "0.32em" }}
            >
              Motors
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-under text-[12px] tracking-[0.24em] uppercase font-medium ${
                  isActive ? "text-[#C5A059]" : "text-white/80 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/quote" data-testid="nav-request-quote" className="btn-gold">Request Quote</Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          data-testid="nav-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#050814]/95 backdrop-blur-xl border-t border-white/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `text-2xl font-display ${isActive ? "text-[#C5A059]" : "text-white"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/quote" className="btn-gold self-start mt-4">Request Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
